import { useState, useEffect, useCallback, useRef } from 'react';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Modal from '@mui/joy/Modal';
import Box from '@mui/joy/Box';
import DialogActions from '@mui/joy/DialogActions';
import DialogContent from '@mui/joy/DialogContent';
// import DialogContentText from '@mui/joy/DialogContentText';
import DialogTitle from '@mui/joy/DialogTitle';
// import { makeStyles } from '@mui/styles';
import FormLabel from '@mui/joy/FormLabel';
import FormControl from '@mui/joy/FormControl';
import Select from '@mui/joy/Select';
import Checkbox from '@mui/joy/Checkbox';
import { useFeedback } from './feedback/Service';
import SnackbarMessage from './feedback/FeedbackSnackbar';
import { Link, ModalDialog, Option, Stack } from '@mui/joy';
import {
  Mic,
  Videocam,
  Settings,
  ExpandMore,
  ExpandLess,
  VolumeUp
} from '@mui/icons-material';

const StreamingMic = ({ recorder, onMicConnected = () => { }, showLabel = false, micConnected, showConnectModes = false, showOutputs = false, isMuted, disableLevelWarning = true, onLevelChange = () => { } }) => {
  const feedback = useFeedback();
  // const classes = useStyles();
  const [level, setLevel] = useState(0);
  const [label, setLabel] = useState("");
  const [connected, setConnected] = useState(false);
  const [showDialog, setShowDialog] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [mics, setMics] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMic, setSelectedMic] = useState("");
  const [selectedOutput, setSelectedOutput] = useState("");
  const [inited, setInited] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [levelWarning, setLevelWarning] = useState(false);
  const [trackMuted, setTrackMuted] = useState(false);
  const timeout = useRef(null);

  const handleMicVolumeChange = useCallback((volume) => {
    const v = Math.round(volume);
    setLevel(v);
    try { onLevelChange(v); } catch (e) { console.error("[MIC]", "Error handling mic volume change", e); }
  }, [setLevel, onLevelChange]);

  const handleAllow = async (audioOnly = false) => {
    setIsConnecting(true);
    recorder.disableVideoOnStart = recorder.disableVideoOnStart || audioOnly;
    if (!connected) {
      try {
        if (!micConnected)
          await recorder.configureUserInput(selectedMic, selectedCamera, settings);
        setConnected(true);
        setTrackMuted(recorder.activeTrack.muted);
        recorder.activeTrack.onmute = () => setTrackMuted(true);
        recorder.activeTrack.onunmute = () => setTrackMuted(false);
        setLabel(recorder.getMicLabel());
        setShowDialog(false);
        onMicConnected(audioOnly, showOutputs ? selectedOutput : null);
      }
      catch (ex) {
        console.warn("[MIC]", "Error connection microphone", ex);
        feedback.snackbar({ text: ex?.message, type: "error" });
      }
    }
    setIsConnecting(false);
  }

  useEffect(() => {
    recorder.on("micvolume", handleMicVolumeChange);
    return () => recorder.off("micvolume", handleMicVolumeChange);
  }, [recorder, handleMicVolumeChange]);

  useEffect(() => {
    if (micConnected)
      handleAllow();
    // eslint-disable-next-line
  }, [micConnected]);

  useEffect(() => {
    if (connected) {
      if ((level === 0 || trackMuted) && !isMuted && !recorder.stream?.getAudioTracks()[0].muted) {
        timeout.current = setTimeout(() => setLevelWarning(trackMuted ? "Microphone is muted by the operating system" : (level === 0 ? "Low voice level" : "")), 5000);
      }
      else {
        clearTimeout(timeout.current);
        setLevelWarning(false);
      }
    }
    return () => {
      clearTimeout(timeout.current);
    }
  }, [connected, level, isMuted, trackMuted, recorder]);

  useEffect(() => {
    const getDevices = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        console.warn("[MIC]", "enumerateDevices() not supported.");
        return;
      }
      let devices = await navigator.mediaDevices.enumerateDevices();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: (recorder.withVideo && devices.some(x => x.kind === "videoinput")) ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false, audio: true });
        const settings = stream.getAudioTracks()[0]?.getSettings();
        stream.getTracks().forEach(x => x.stop());
        console.log("[MIC]", "got audio settings", settings);
        setSettings(settings);
      }
      catch (err) {
        console.warn(err.name);
        return feedback.alert({ title: err.message || "Can't get media devices", text: err.name === "NotReadableError" ? "Check if other apps are using camera or microphone" : "" });
      }
      devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(x => x.kind === "videoinput");
      console.log("[MIC]", 'cameras:', cameras);
      const mics = devices.filter(x => x.kind === "audioinput");
      console.log("[MIC]", 'mics:', mics);
      const outputs = devices.filter(x => x.kind === "audiooutput");
      console.log("[MIC]", 'audio-outputs:', outputs);
      setCameras(cameras);
      setMics(mics);
      setOutputs(outputs);
      if (mics.length > 0)
        setSelectedMic(mics[0]?.deviceId);
      if (cameras.length > 0)
        setSelectedCamera(cameras[0]?.deviceId);
      if (showOutputs && outputs.length > 0)
        setSelectedOutput(outputs[0]?.deviceId);
      setInited(true);
    }
    getDevices();
    //eslint-disable-next-line
  }, [recorder, showOutputs]);

  const hasMic = mics.length > 0 || !inited;

  return (
    <>
      <Box sx={{
        padding: 0,
        position: 'relative',
        background: theme => theme.palette.neutral[theme.palette.neutral === "dark" ? 600 : 300],
        height: theme => theme.spacing(1),
        width: '100%',
        "& span": {
          height: '100%',
          position: 'relative',
          display: 'block',
          background: theme => theme.palette.type === "dark" ? theme.palette.neutral[300] : theme.palette.primary[400],
        }
      }}
      >
        <span style={{ width: level + '%' }}></span>
      </Box>
      {!disableLevelWarning &&
        <SnackbarMessage
          open={!!levelWarning}
          handleClose={() => setLevelWarning(false)}
          text={levelWarning}
          autoHide={false}
          type="warning"
        />
      }
      {showLabel &&
        <Typography sx={{ color: (theme) => theme.palette.text.primary, pl: 2 }}>{connected ? (label || "Unknown mic") : "Microphone is disconnected"}</Typography>
      }
      <Modal
        open={showDialog && !micConnected}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <ModalDialog maxWidth="sm" variant="outlined" sx={{ borderRadius: 'md' }}>
          <DialogTitle id="alert-dialog-title" sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 0.5
          }}>
            <Mic sx={{ color: 'primary.500', fontSize: '1.25rem' }} />
            Camera & Microphone Setup
          </DialogTitle>

          <DialogContent sx={{ py: 1.5 }}>
            {hasMic ? (
              <Stack spacing={2}>
                <Typography level="body-sm" sx={{ lineHeight: 1.5 }}>
                  To proceed, please allow access to your microphone and camera when prompted by your browser. This is necessary for audio and video communication. If you&apos;ve previously denied access, you may need to reset permissions in your browser settings. Your devices will only be used during sessions and are not recorded without your consent.
                </Typography>

                {/* Device Selection - Minimal */}
                {mics.length > 1 && (
                  <FormControl>
                    <FormLabel sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.25
                    }}>
                      <Mic sx={{ fontSize: '1rem', color: 'neutral.600' }} />
                      Microphone
                    </FormLabel>
                    <Select
                      value={selectedMic}
                      onChange={(_, value) => setSelectedMic(value)}
                      sx={{ mt: 0.25 }}
                    >
                      {mics.map(mic => (
                        <Option value={mic.deviceId} key={mic.deviceId}>
                          {mic.label}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {(recorder.withVideo && cameras.length > 1) && (
                  <FormControl>
                    <FormLabel sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.25
                    }}>
                      <Videocam sx={{ fontSize: '1rem', color: 'neutral.600' }} />
                      Camera
                    </FormLabel>
                    <Select
                      value={selectedCamera}
                      onChange={(_, value) => setSelectedCamera(value)}
                      sx={{ mt: 0.25 }}
                    >
                      {cameras.map(camera => (
                        <Option value={camera.deviceId} key={camera.deviceId}>
                          {camera.label}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {(showOutputs && outputs.length > 1) && (
                  <FormControl>
                    <FormLabel sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.25
                    }}>
                      <VolumeUp sx={{ fontSize: '1rem', color: 'neutral.600' }} />
                      Output
                    </FormLabel>
                    <Select
                      value={selectedOutput}
                      onChange={(_, value) => setSelectedOutput(value)}
                      sx={{ mt: 0.25 }}
                    >
                      {outputs.map(output => (
                        <Option value={output.deviceId} key={output.deviceId}>
                          {output.label}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Advanced Settings - Minimal with Icon */}
                {settings && (
                  <Box>
                    <Link
                      onClick={() => setShowSettings(x => !x)}
                      sx={{
                        fontSize: 'sm',
                        cursor: 'pointer',
                        color: 'neutral.600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        width: 'fit-content'
                      }}
                    >
                      <Settings sx={{ fontSize: '0.875rem' }} />
                      {showSettings ? "Hide" : "Show"} advanced settings
                      {showSettings ?
                        <ExpandLess sx={{ fontSize: '1rem' }} /> :
                        <ExpandMore sx={{ fontSize: '1rem' }} />
                      }
                    </Link>

                    {showSettings && (
                      <Stack spacing={0.75} sx={{ mt: 1.5, pl: 0.5 }}>
                        <Checkbox
                          checked={settings.autoGainControl}
                          onChange={({ target }) => setSettings(x => ({ ...x, autoGainControl: target.checked }))}
                          label="Auto Gain Control"
                          size="sm"
                        />
                        <Checkbox
                          checked={settings.echoCancellation}
                          onChange={({ target }) => setSettings(x => ({ ...x, echoCancellation: target.checked }))}
                          label="Echo Cancellation"
                          size="sm"
                        />
                        <Checkbox
                          checked={settings.noiseSuppression}
                          onChange={({ target }) => setSettings(x => ({ ...x, noiseSuppression: target.checked }))}
                          label="Noise Suppression"
                          size="sm"
                        />
                      </Stack>
                    )}
                  </Box>
                )}
              </Stack>
            ) : (
              <Typography>Error: No microphones found.</Typography>
            )}
          </DialogContent>

          <DialogActions sx={{
            pt: 0,
            pb: 1,
            px: 0,
            justifyContent: 'center'
          }}>
            {showConnectModes ? (
              <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => handleAllow(true)}
                  disabled={isConnecting || !hasMic || !inited}
                  sx={{
                    flex: 1,
                    py: 1.25,
                    fontWeight: '500'
                  }}
                >
                  Join with Audio Only
                </Button>
                <Button
                  onClick={() => handleAllow()}
                  disabled={isConnecting || !hasMic || !inited}
                  sx={{
                    flex: 1,
                    py: 1.25,
                    fontWeight: '600'
                  }}
                >
                  Join with Video
                </Button>
              </Stack>
            ) : (
              <Button
                onClick={() => handleAllow()}
                disabled={isConnecting || !hasMic || !inited}
                sx={{
                  px: 5,
                  py: 1,
                  fontWeight: '600',
                  borderRadius: 'md'
                }}
              >
                Confirm Devices
              </Button>
            )}
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
}

export default StreamingMic;